import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  PhoneIcon, 
  EyeIcon, 
  EyeSlashIcon 
} from '@heroicons/react/24/outline';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profileCompleted, signInWithGoogle, signInWithPhone, verifyOTP } = useAuth();
  const [authMethod, setAuthMethod] = useState('google'); // 'google' or 'phone'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [resendCount, setResendCount] = useState(0);

  const track = (name, params = {}) => {
    try { console.log('analytics', name, params); } catch {}
  };

  // Next param and redirect after auth
  const searchParams = new URLSearchParams(location.search);
  const nextPath = searchParams.get('next') || '/dashboard';
  useEffect(() => {
    if (user) {
      const dest = profileCompleted ? nextPath : '/profile-setup';
      track('redirect_destination', { dest, profileCompleted });
      navigate(dest, { replace: true });
    }
  }, [user, profileCompleted, nextPath, navigate]);

  // OTP countdown
  useEffect(() => {
    if (!showOtpInput || otpTimer <= 0) return;
    const id = setInterval(() => setOtpTimer((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(id);
  }, [showOtpInput, otpTimer]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      track('login_google_click');
      await signInWithGoogle();
      // Navigation will be handled by the useEffect when user state changes
    } catch (error) {
      console.error('Google sign in error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSignIn = async () => {
    setPhoneError('');
    const raw = phoneNumber.trim();
    const formatted = raw.startsWith('+') ? raw : `+91 ${raw}`;
    const e164 = formatted.replace(/\s+/g, '');
    if (!/^\+\d{10,15}$/.test(e164)) {
      setPhoneError('Please enter a valid Indian number like +91 98765 43210.');
      return;
    }

    setLoading(true);
    try {
      track('phone_submit_number');
      const result = await signInWithPhone(e164);
      setConfirmationResult(result);
      setShowOtpInput(true);
      setOtpTimer(30);
      track('phone_otp_sent');
    } catch (error) {
      console.error('Phone sign in error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async () => {
    setOtpError('');
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP.');
      return;
    }

    setLoading(true);
    try {
      await verifyOTP(confirmationResult, otp);
      track('phone_otp_verify_success');
      // Navigation will be handled by the useEffect when user state changes
    } catch (error) {
      console.error('OTP verification error:', error);
      setOtpError("That code didn’t match. Try again.");
      track('phone_otp_verify_fail');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <img
            src="/logo-192x192.png"
            srcSet="/logo-192x192.png 1x, /logo-512x512.png 2x"
            alt="W3Skills logo"
            className="mx-auto h-16 w-16 rounded-lg shadow"
          />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Sign in to your MHT-CET (PCM) plan
          </h2>
          <p className="mt-2 text-xs text-gray-600">You’ll continue to <span className="font-semibold">{profileCompleted ? (nextPath === '/dashboard' ? 'Dashboard' : nextPath) : 'Profile Setup'}</span> after sign‑in.</p>
        </div>

        {/* Auth Method Toggle */}
        <div className="bg-white rounded-lg p-1 shadow-sm">
          <div className="flex">
            <button
              onClick={() => setAuthMethod('google')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                authMethod === 'google'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Google
            </button>
            <button
              onClick={() => setAuthMethod('phone')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                authMethod === 'phone'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Phone
            </button>
          </div>
        </div>

        {/* Auth Forms */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {authMethod === 'google' ? (
            // Google Sign In
            <div className="space-y-4">
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 rounded-md shadow-sm bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {loading ? 'Signing in…' : 'Continue with Google'}
              </button>
              
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Secure sign‑in with Google / Phone OTP (Firebase Auth). We never see your password. By continuing, you agree to our <button className="underline" onClick={() => navigate('/terms-of-service')}>Terms</button> and <button className="underline" onClick={() => navigate('/privacy-policy')}>Privacy Policy</button>.
                </p>
              </div>
            </div>
          ) : (
            // Phone OTP Sign In
            <div className="space-y-4">
              {!showOtpInput ? (
                // Phone Number Input
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile number (India)
                  </label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="phone"
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+91 98765 43210"
                      inputMode="numeric"
                      autoComplete="tel"
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  {phoneError && <p className="mt-2 text-xs text-red-600">{phoneError}</p>}
                  <p className="mt-2 text-xs text-gray-500">Standard SMS rates may apply.</p>
                  <button
                    onClick={handlePhoneSignIn}
                    disabled={loading || !phoneNumber}
                    className="mt-4 w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Sending OTP…' : 'Get OTP'}
                  </button>
                </div>
              ) : (
                // OTP Input
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                    Enter 6-digit code
                  </label>
                  <div className="relative">
                    <input
                      id="otp"
                      type={showPassword ? "text" : "password"}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      autoComplete="one-time-code"
                      className="block w-full pr-10 pl-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {otpError && <p className="mt-2 text-xs text-red-600">{otpError}</p>}
                  <div className="mt-2 text-xs text-gray-600">
                    {otpTimer > 0 ? (
                      <span>Resend OTP in 00:{String(otpTimer).padStart(2, '0')}</span>
                    ) : (
                      <button
                        onClick={() => {
                          if (resendCount >= 3) {
                            setOtpError('Too many attempts. Try again in 15 minutes or continue with Google.');
                            return;
                          }
                          handlePhoneSignIn();
                          setResendCount((c) => c + 1);
                        }}
                        className="underline"
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={handleOtpVerification}
                      disabled={loading || otp.length !== 6}
                      className="flex-1 flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? 'Verifying…' : 'Verify OTP'}
                    </button>
                    <button
                      onClick={() => {
                        setShowOtpInput(false);
                        setOtp('');
                        setConfirmationResult(null);
                        setOtpTimer(0);
                        setOtpError('');
                      }}
                      className="px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      Back
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">Didn’t get it? Check network, ensure the number is correct, or <button className="underline" onClick={() => setAuthMethod('google')}>use Google instead</button>.</p>
                </div>
              )}
              
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Secure sign‑in with Google / Phone OTP (Firebase Auth). We never see your password. By continuing, you agree to our <button className="underline" onClick={() => navigate('/terms-of-service')}>Terms</button> and <button className="underline" onClick={() => navigate('/privacy-policy')}>Privacy Policy</button>.
                </p>
              </div>
            </div>
          )}
        </div>
        {/* Help box */}
        <details className="bg-white rounded-lg shadow-lg p-6">
          <summary className="cursor-pointer text-sm font-semibold text-gray-900">Why sign in?</summary>
          <ul className="mt-3 list-disc list-inside text-sm text-gray-700 space-y-1">
            <li>Personalized daily plan (PCM)</li>
            <li>CET-level mocks & PYQs (2013–2024)</li>
            <li>Score & college predictors</li>
          </ul>
        </details>

        {/* Recaptcha container for phone auth */}
        <div id="recaptcha-container"></div>
      </div>
    </div>
  );
};

export default LoginPage;
